import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * Shared expand state for one row of a grid, or `null` outside a row.
 *
 * `null` rather than a default object so `useExpandableRow()` can tell "no row around me"
 * from "a row that happens to be collapsed" — the two want different toggle labels, and a
 * value with nobody to co-ordinate with should just keep its own state.
 *
 * @type {React.Context<{ isExpanded: boolean, toggle: () => void } | null>}
 */
const ExpandableRowContext = createContext(null);

/**
 * The row this value belongs to, if any.
 *
 * @returns {{ isExpanded: boolean, toggle: () => void } | null}
 */
export const useExpandableRow = () => useContext(ExpandableRowContext);

/**
 * Makes every `ExpandableText` below it expand and collapse as one.
 *
 * A row of a grid reads as one record, and its long values are usually long together — a
 * screener answer next to a note next to a free-text property. Expanding them one at a
 * time means one click per cell and a row that grows in steps; design asked for one
 * control per row instead (PFOX-1406), so the state lives here rather than in each cell.
 *
 * Renders no element of its own: a `<tr>` may only contain cells, and this sits between
 * the two.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {React.ReactNode}
 */
export function ExpandableRowScope({ children }) {
    const [isExpanded, setIsExpanded] = useState(false);

    // Identity has to be stable while the row is unchanged, or every cell of every row
    // re-renders on any state change anywhere in the table.
    const value = useMemo(
        () => ({
            isExpanded,
            toggle: () => setIsExpanded(previousIsExpanded => !previousIsExpanded)
        }),
        [isExpanded]
    );

    return <ExpandableRowContext.Provider value={value}>{children}</ExpandableRowContext.Provider>;
}

export default ExpandableRowScope;
